<template>
  <div class="reports">
    <v-card max-width="500" min-width="500" class="mt-10 mx-auto">
      <v-card-title class="mb-2 primary--text">
        <span class="subtitle-1" v-translate="`docs.${type}.title`">Select an option</span>
      </v-card-title>
      <v-card-text>
        <div class="text-center" v-if="loading && (!sites.length && !dates.length)">
          <v-progress-circular
            class="my-10"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
        <div class="text-center" v-if="sites.length || dates.length">
          <template v-if="type !== 'unloading'">
            <v-select
              v-model="site"
              class="mb-8"
              :label="$lang.translate(`docs.${type}.site`)"
              :items="sites"
              :disabled="downloading"
              dense
              solo
            ></v-select>
          </template>
          <template v-if="type === 'unloading'">
            <v-select
              v-model="date"
              :label="$lang.translate(`docs.${type}.date`)"
              :items="dates"
              :disabled="downloading"
              dense
              solo
            ></v-select>
            <v-select
              v-model="site"
              :label="$lang.translate(`docs.${type}.site`)"
              :items="sites"
              :disabled="!sites.length || downloading"
              dense
              solo
            ></v-select>
            <v-select
              v-model="shuttle"
              :label="$lang.translate(`docs.${type}.shuttle`)"
              class="mb-8"
              :items="shuttles"
              :disabled="!shuttles.length || downloading"
              dense
              solo
            ></v-select>
          </template>
          <v-btn
            color="primary"
            v-translate="`docs.${type}.generate`"
            :disabled="!site || (type === 'unloading' && (!date || !shuttle)) || disabled"
            @click="download"
            :loading="downloading"
          >Validate</v-btn>
        </div>
        <p
          class="orange--text subtitle-1 mt-4 mb-0 text-center"
          v-translate="`docs.${type}.error`"
          v-if="error && !loading"
        >
          Something went wrong, please try later or contact the administrator.
        </p>
        <p
          class="primary--text subtitle-1 mt-4 mb-0 text-center"
          v-translate="`docs.${type}.success`"
          v-if="success"
        >
          The download will start in a second.
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { Reports } from '@/api';
import moment from 'moment';

export default {
  name: 'app-reports',
  data() {
    return {
      config: {},
      downloading: false,
      loading: true,
      error: false,
      success: false,
      disabled: false,
      sites: [],
      dates: [],
      shuttles: [],
      site: '',
      date: '',
      shuttle: '',
    };
  },
  watch: {
    async site(value) {
      if (value) {
        this.success = false;
        this.error = false;

        if (this.type === this.config.unloading.alias) {
          this.shuttles = [];
          this.shuttle = '';
          this.shuttles = await this.fetch({
            type: this.config.unloading.shuttles,
            date: this.date,
            site: value,
          }, this.config.unloading.shuttles);
        }
      }
    },
    async date(value) {
      if (value) {
        this.success = false;
        this.error = false;

        this.sites = [];
        this.shuttles = [];
        this.site = '';
        this.shuttle = '';
        this.sites = await this.fetch({
          type: this.config.unloading.sites,
          date: value,
        }, this.config.unloading.sites);
      }
    },
  },
  computed: {
    type() {
      return this.$route.params.id.toLowerCase();
    },
    value() {
      switch (this.type) {
        case this.config.receipt.alias:
          return this.config.receipt.fetch;
        case this.config.shipment.alias:
          return this.config.shipment.fetch;
        case this.config.unloading.alias:
          return this.config.unloading.fetch;
        default:
          return new Error('Not a valid type');
      }
    },
    downloadValue() {
      switch (this.type) {
        case this.config.receipt.alias:
          return this.config.receipt.download;
        case this.config.shipment.alias:
          return this.config.shipment.download;
        case this.config.unloading.alias:
          return this.config.unloading.download;
        default:
          return new Error('Not a valid type');
      }
    },
  },
  methods: {
    async download() {
      this.downloading = true;
      this.disabled = true;
      const anchor = window.document.createElement('a');
      let res = {};

      try {
        ({ data: res } = await Reports.download({
          type: this.downloadValue,
          site: this.site,
          shuttle: this.shuttle,
          date: this.date,
        }));

        this.success = true;
        anchor.href = `data:application/octet-stream;base64,${res.fileContents}`;
        anchor.download = res.fileDownloadName;
        anchor.click();
      } catch (err) {
        this.error = true;
      }

      this.clear();
    },
    clear() {
      this.downloading = false;
      this.disabled = false;
      this.error = false;
    },
    async fetch(params, unloadingValue) {
      this.loading = true;
      let data = [];

      try {
        const { data: res } = await Reports.fetch(params);

        data = window._.get(res, 'result_set.result_row', []);
        if (!window._.isArray(data)) data = [data];
        data = data.map((s) => {
          switch (this.type) {
            case this.config.unloading.alias: {
              const date = moment(s.PLANNED_END_AFTER);
              const isFR = this.$lang.locale === 'fr';

              switch (unloadingValue) {
                case this.config.unloading.sites: {
                  return {
                    text: s.PARTY_ID,
                    value: s.PARTY_ID,
                  };
                }
                case this.config.unloading.shuttles: {
                  return {
                    text: s.NAVETTE,
                    value: s.NAVETTE,
                  };
                }
                default: {
                  return {
                    text: isFR ? date.format('DD/MM/YYYY') : date.format('MM/DD/YYYY'),
                    value: s.PLANNED_END_AFTER,
                  };
                }
              }
            }
            default: {
              return {
                text: s.party_id,
                value: s.party_id,
              };
            }
          }
        });
      } catch (err) {
        this.error = true;
      }

      this.loading = false;
      return data;
    },
  },
  async created() {
    this.config = window.ENV.reports;
    let data = [];

    if (this.value instanceof Error) {
      this.error = true;
    }

    if (!this.config) {
      console.log('REPORT no config found.');
      return false;
    }

    data = await this.fetch({ type: this.value });

    switch (this.type) {
      case this.config.unloading.alias: {
        this.dates = data;
        break;
      }
      default: {
        this.sites = data;
      }
    }

    return true;
  },
};
</script>
